import { useCallback, useEffect } from 'react'
import { setAlertQueue } from 'cache/alerts'
import { useDealLoadStatusPolling } from 'hooks/use-deal-load-status-polling'
import { useGetCacheEditPageDetails } from 'hooks/use-get-cache-edit-page-details'
import { useUserProfile } from 'hooks/use-user-profile'
import { MessageType } from 'ts/enums/messageType.enums'
import { ERROR_MESSAGES } from '../constants'

export const useDecisionEditPageData = (dealId: number | undefined) => {
  const {
    getCacheEditPageDetails,
    data,
    loading: cacheLoading,
    error,
  } = useGetCacheEditPageDetails()
  const { userId } = useUserProfile()

  const refreshEditPageData = useCallback(async () => {
    if (!dealId) return

    try {
      const cacheResult = await getCacheEditPageDetails({
        variables: { dealId },
      })

      if (!cacheResult.data?.cacheEditPageDetails?.isSuccess) {
        setAlertQueue({
          type: MessageType.ERROR,
          message: cacheResult.data?.cacheEditPageDetails?.errorMessage ?? ERROR_MESSAGES.NO_DATA,
        })
      }
    } catch (err) {
      console.error('Error loading edit page data:', err)
      setAlertQueue({
        type: MessageType.ERROR,
        message: ERROR_MESSAGES.LOAD_FAILED_RETRY,
      })
    }
  }, [dealId, getCacheEditPageDetails])

  // Poll GetDealLoadStatus; once 'Completed' triggers cache load.
  // skipSideEffects prevents mutating shared reactive vars used by the decision page.
  const { isPolling, pollError } = useDealLoadStatusPolling(dealId, userId, {
    skipSideEffects: true,
    onStatusCompleted: () => {
      refreshEditPageData()
    },
  })

  useEffect(() => {
    if (error) {
      setAlertQueue({
        type: MessageType.ERROR,
        message: ERROR_MESSAGES.REFRESH_PAGE,
      })
    }
  }, [error])

  // Surface an alert if polling exceeded the retry limit (backend stuck / unreachable)
  useEffect(() => {
    if (pollError) {
      setAlertQueue({
        type: MessageType.ERROR,
        message: ERROR_MESSAGES.DEAL_LOAD_TIMEOUT,
      })
    }
  }, [pollError])

  // If dealId is known but userId hasn't been populated yet (e.g. on page refresh before
  // userProfileVar resolves), polling cannot start. Treat this window as loading so the
  // page shows a loader rather than rendering with no data and no spinner.
  const isWaitingForUserId = !!dealId && !userId

  // Keep loading true while: waiting for userId, polling (deal not yet completed), or fetching cache data
  const loading = isWaitingForUserId || isPolling || cacheLoading

  return {
    data,
    loading,
    error,
    refreshEditPageData,
  }
}
